// v0.6.1 — RatePendingAcksPanel: review queue for revived TEDB rate-drift
// detection.
//
// Mounted inside the Settings → Rates tab, right after RateVerifyPanel.
// ADMIN-only (the underlying endpoints also enforce; this hides the
// affordance for OPERATOR). Lists open `pending_rate_acks` docs (opened
// by engine/rate_drift_worker.py::propose_rate_acks when the watchdog
// finds a national-return filing rate that disagrees with TEDB) and lets
// an admin resolve each one via the existing acks endpoint.
//
// This panel never writes a rate row itself -- it only records the
// operator's decision (no_amendment_needed | amendment_required) via the
// existing, already-tested POST /admin/rates/acks/{id} route. If the rate
// genuinely needs updating, that remains a manual vat_rate_history change
// outside the app, same as today -- this panel's job is purely to surface
// the drift and stop it from being missed, not to apply anything.
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchPendingRateAcks, resolveRateAck } from "../lib/api";
import { labelFor } from "../lib/enumLabels";
import { PresetReasonSelect } from "./PresetReasonSelect";
import { RATE_ACK_REASONS } from "../lib/reviewReasonPresets";

export const RatePendingAcksPanel = ({ isAdmin }) => {
  const { t } = useTranslation();
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetchPendingRateAcks();
    setLoading(false);
    if (r.ok) {
      setAcks(r.body?.pending || []);
      setErr(null);
    } else {
      setErr(r.error || "failed to load pending acks");
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      const r = await fetchPendingRateAcks();
      if (!cancelled && r.ok) setAcks(r.body?.pending || []);
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) return null;

  return (
    <div
      id="rate-pending-acks"
      data-testid="rate-pending-acks-panel"
      className="mt-4 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 rounded-xl p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] tracking-normal text-teal-700 dark:text-teal-300">
            {t("ui.settings.rate_acks.title", { defaultValue: "Pending rate-drift acks" })}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 max-w-2xl">
            {t("ui.settings.rate_acks.intro", { defaultValue: "TEDB reported a different rate than what a filing jurisdiction has on file. This never updates the rate automatically — confirm the real effective date yourself before deciding." })}
          </p>
        </div>
        <button
          type="button"
          data-testid="rate-pending-acks-refresh"
          onClick={load}
          disabled={loading}
          className={`text-xs px-3 py-1.5 rounded-xl ${
            loading ? "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 cursor-wait"
                    : "bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
          }`}
        >
          {loading ? t("ui.settings.rate_acks.loading", { defaultValue: "LOADING…" }) : t("ui.settings.rate_acks.refresh", { defaultValue: "REFRESH" })}
        </button>
      </div>
      {err && (
        <div
          data-testid="rate-pending-acks-error"
          className="text-[11px] text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-700/40 px-3 py-2 rounded-xl"
        >
          {err}
        </div>
      )}
      {acks.length === 0 && !err && (
        <div
          data-testid="rate-pending-acks-empty"
          className="text-[11px] text-emerald-700 dark:text-emerald-300"
        >
          ✓ {t("ui.settings.rate_acks.empty", { defaultValue: "No pending rate-drift acks." })}
        </div>
      )}
      {acks.map((ack) => (
        <AckRow key={ack.id} ack={ack} onResolved={load} />
      ))}
    </div>
  );
};

const AckRow = ({ ack, onResolved }) => {
  const { t } = useTranslation();
  const [decision, setDecision] = useState("no_amendment_needed");
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const [amendmentRef, setAmendmentRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const reasonLen = reason.trim().length;
  const reasonOk = reason.trim().length >= 50;
  const canSubmit = reasonOk && !busy;

  // v0.6.12 A5-27 — the two decisions have their own reasons; switching
  // clears one picked for the other.
  const pickDecision = (next) => {
    if (next === decision) return;
    setDecision(next); setPreset(""); setReason("");
  };

  const submit = async () => {
    setBusy(true);
    setErr(null);
    const r = await resolveRateAck(ack.id, {
      decision,
      reason: reason.trim(),
      amendment_ref: amendmentRef.trim() || undefined,
    });
    setBusy(false);
    if (r.ok) {
      onResolved();
    } else {
      setErr(r.error || "resolve failed");
    }
  };

  const affected = ack.affected_frozen_calc_ids || [];

  return (
    <div
      data-testid={`rate-pending-ack-${ack.id}`}
      className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden"
    >
      <div className="bg-white dark:bg-slate-900 px-3 py-1.5 flex items-center justify-between gap-2 flex-wrap">
        <span className="tabular-nums text-xs text-teal-700 dark:text-teal-300">
          {ack.country} · {labelFor("tax_category", ack.category)}
        </span>
        <span className="tabular-nums text-[11px] text-slate-600 dark:text-slate-300">
          {ack.old_rate ?? "—"} → <span className="text-slate-900 dark:text-slate-50">{ack.new_rate}</span>
        </span>
      </div>
      <div className="px-3 py-2 space-y-2">
        <div className="text-[11px] text-amber-700 dark:text-amber-300">
          {t("ui.settings.rate_acks.no_effective_date", { defaultValue: "No effective date on file — confirm before acting." })}
        </div>
        {affected.length > 0 && (
          <div
            data-testid={`rate-pending-ack-frozen-${ack.id}`}
            className="text-[11px] text-slate-600 dark:text-slate-300"
          >
            {t("ui.settings.rate_acks.affected", { count: affected.length, country: ack.country, defaultValue: `${affected.length} frozen filing(s) for ${ack.country} may be affected — check each against the real effective date:` })}{" "}
            {(ack.affected_filing_refs || []).join(", ") || affected.join(", ")}
          </div>
        )}
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-[11px] text-slate-600 dark:text-slate-300 flex items-center gap-1">
            <input
              type="radio"
              checked={decision === "no_amendment_needed"}
              onChange={() => pickDecision("no_amendment_needed")}
            />
            {t("ui.settings.rate_acks.decision_no_amendment", { defaultValue: "No amendment needed" })}
          </label>
          <label className="text-[11px] text-slate-600 dark:text-slate-300 flex items-center gap-1">
            <input
              type="radio"
              checked={decision === "amendment_required"}
              onChange={() => pickDecision("amendment_required")}
            />
            {t("ui.settings.rate_acks.decision_amendment", { defaultValue: "Amendment required" })}
          </label>
        </div>
        <PresetReasonSelect
          testid={`rate-pending-ack-reason-preset-${ack.id}`}
          presets={RATE_ACK_REASONS[decision] || []}
          preset={preset}
          onPick={(p, v) => { setPreset(p); setReason(v); }}
        />
        {/* The v0.6.8 lesson applies to the picker too: a disabled
            Resolve must say why. */}
        {!preset && (
          <div
            data-testid={`rate-pending-ack-reason-hint-${ack.id}`}
            className="text-xs text-amber-700 dark:text-amber-300"
          >
            {t("ui.settings.rate_acks.choose_reason", { defaultValue: "Choose a reason to resolve." })}
          </div>
        )}
        {decision === "amendment_required" && (
          <input
            data-testid={`rate-pending-ack-amendment-ref-${ack.id}`}
            value={amendmentRef}
            onChange={(e) => setAmendmentRef(e.target.value)}
            placeholder={t("ui.settings.rate_acks.amendment_ref_placeholder", { defaultValue: "Amendment reference (optional)" })}
            className="w-full text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-2 py-1.5"
          />
        )}
        {err && (
          <div className="text-[11px] text-rose-700 dark:text-rose-300">
            {err}
          </div>
        )}
        <button
          type="button"
          data-testid={`rate-pending-ack-resolve-${ack.id}`}
          onClick={submit}
          disabled={!canSubmit}
          className={`text-xs px-3 py-1.5 rounded-xl ${
            canSubmit
              ? "bg-teal-600 hover:bg-teal-700 text-white font-bold"
              : "bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed"
          }`}
        >
          {busy ? t("ui.settings.rate_acks.resolving", { defaultValue: "RESOLVING…" }) : t("ui.settings.rate_acks.resolve", { defaultValue: "RESOLVE" })}
        </button>
      </div>
    </div>
  );
};

export default RatePendingAcksPanel;
